import moment from 'moment';
import { memo, useCallback, useMemo } from 'react';

import { isoToFormattedLocalDate, isoToFormattedLocalDateTime, isoToFormattedLocalTime } from '~/helpers/datetime';
import { t } from '~/helpers/localization';
import { CLAN_WARS_STAGE } from '~/Reducers/ReducerClanWars';

import styles from './Calendar.scss';
import { Progress } from './Parts';

import type { Moment } from 'moment';
import type { WarSettingsStages } from '~/types/declaration';

type Days = string[];

const stagesOrder: CLAN_WARS_STAGE[] = [
  CLAN_WARS_STAGE.PREPARATION,
  CLAN_WARS_STAGE.WAR,
  CLAN_WARS_STAGE.MATCHMAKING,
  CLAN_WARS_STAGE.REWARDING,
];

const daysTitles: Days = [
  t('понедельник'),
  t('вторник'),
  t('среда'),
  t('четверг'),
  t('пятница'),
  t('суббота'),
  t('воскресенье'),
];

export const getDayOffset = (weekIndex: number, dayIndex: number, currentWeek: number) => {
  const currentDayOffset = moment().isoWeekday();

  if (weekIndex === currentWeek) {
    return -currentDayOffset + dayIndex;
  }

  return weekIndex < currentWeek
    ? -currentDayOffset + dayIndex - 7 * (currentWeek - weekIndex)
    : -currentDayOffset + dayIndex + 7 * (weekIndex - currentWeek);
};

export const getDateDayNumber = (offset: number) => {
  const m = moment();
  if (offset === 0) {
    return m;
  }
  return offset < 0 ? m.subtract(-offset, 'days') : m.add(offset, 'days');
};

interface CalendarProps {
  currentWeek: number;
  weeksCount: number;
  stages: WarSettingsStages;
  currentStage: CLAN_WARS_STAGE | '';
  currentRound: number;
}

const Calendar = ({ currentWeek, weeksCount, stages, currentStage, currentRound }: CalendarProps) => {
  const getStageDuration = useCallback(
    (stage: keyof WarSettingsStages) => {
      const startDate = stages[stage].startDate;
      const endDate = stages[stage].endDate;

      const inOneDay = isoToFormattedLocalDate(startDate) === isoToFormattedLocalDate(endDate);
      const startDateComponent = isoToFormattedLocalDateTime(startDate);
      let endDateComponent = isoToFormattedLocalDateTime(endDate);

      if (inOneDay) {
        endDateComponent = isoToFormattedLocalTime(endDate);
      }

      return { startDateComponent, endDateComponent };
    },
    [stages],
  );

  const renderStageDuration = useCallback(
    (stage: keyof WarSettingsStages) => {
      const interval = getStageDuration(stage);

      return (
        <span>
          {interval.startDateComponent} — {interval.endDateComponent}
        </span>
      );
    },
    [getStageDuration],
  );

  const getOverlayOffset = useCallback((date: Moment, isRight: boolean) => {
    const dayStart = moment.utc(date).local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const msDiff = date.diff(dayStart);
    const msDay = 24 * 60 * 60 * 1000;
    const percentDiff = (msDiff / msDay) * 100;
    return isRight ? 100 - percentDiff : percentDiff;
  }, []);

  const getInnerOverlay = useCallback(
    (key: keyof WarSettingsStages | '', inCalendar?: boolean) => {
      switch (key) {
        case CLAN_WARS_STAGE.PREPARATION:
          return <div className={styles.preparationInnerOverlay} />;

        case CLAN_WARS_STAGE.MATCHMAKING:
          if (currentStage === CLAN_WARS_STAGE.WAR && inCalendar === true) {
            return <div className={styles.matchmakingInnerOverlayInWar} />;
          }
          return <div className={styles.matchmakingInnerOverlay} />;

        case CLAN_WARS_STAGE.WAR:
          return <div className={styles.warInnerOverlay} />;

        case CLAN_WARS_STAGE.REWARDING:
          if (inCalendar === true) {
            return <div className={styles.rewardingInnerOverlayInWar} />;
          }
          return <div className={styles.rewardingInnerOverlay} />;

        default:
          return null;
      }
    },
    [currentStage],
  );

  const renderStages = useCallback(
    (date: Moment) => {
      const overlays: React.ReactNode[] = [];

      stagesOrder.forEach((s) => {
        const stageName = s as keyof WarSettingsStages;

        if (!Object.prototype.hasOwnProperty.call(stages, stageName)) {
          return;
        }

        const stage = stages[stageName];

        if (!stage.startDate || !stage.endDate) {
          return;
        }

        const stageStartDate = moment.utc(stage.startDate).local();
        const stageEndDate = moment.utc(stage.endDate).local();

        const stageStartDateDayIndex = parseInt(stageStartDate.format('DDDD'), 10);
        const stageEndDateDayIndex = parseInt(stageEndDate.format('DDDD'), 10);
        const dateDayIndex = parseInt(date.format('DDDD'), 10);

        const key = `PrepOverlay_${dateDayIndex}_${stageName}`;

        if (dateDayIndex === stageStartDateDayIndex && dateDayIndex === stageEndDateDayIndex) {
          overlays.push(
            <div
              key={key}
              className={styles.stageOverlay}
              style={{
                left: `${getOverlayOffset(stageStartDate, false)}%`,
                right: `${getOverlayOffset(stageEndDate, true)}%`,
                zIndex: -2,
              }}
            >
              {getInnerOverlay(stageName, true)}
            </div>,
          );
          return;
        }

        if (dateDayIndex === stageStartDateDayIndex) {
          overlays.push(
            <div
              key={key}
              className={styles.stageOverlay}
              style={{ left: `${getOverlayOffset(stageStartDate, false)}%` }}
            >
              {getInnerOverlay(stageName, true)}
            </div>,
          );
          return;
        }

        if (dateDayIndex === stageEndDateDayIndex) {
          overlays.push(
            <div
              key={key}
              className={styles.stageOverlay}
              style={{ right: `${getOverlayOffset(stageEndDate, true)}%` }}
            >
              {getInnerOverlay(stageName, true)}
            </div>,
          );
          return;
        }

        if (dateDayIndex > stageStartDateDayIndex && dateDayIndex < stageEndDateDayIndex) {
          overlays.push(
            <div key={key} className={styles.stageOverlay}>
              {getInnerOverlay(stageName, true)}
            </div>,
          );
        }
      });

      return overlays;
    },
    [getInnerOverlay, getOverlayOffset, stages],
  );

  const renderDaysTitles = useMemo(() => {
    return daysTitles.map((title, index) => {
      const needRightBorder = index < 6;
      const isWeekend = index > 4;

      return (
        <div className={styles.cell} key={`dayTitle${index}`}>
          <div
            className={`${styles.dayTitle} ${needRightBorder ? styles.rightBorder : ''} ${
              isWeekend ? styles.dayTitleWeekend : ''
            }`}
          >
            {title}
          </div>
        </div>
      );
    });
  }, []);

  const renderDays = useCallback(
    (weekIndex: number) => {
      const days: React.ReactNode[] = [];

      for (let index = 0; index < 7; index++) {
        const dayIndex = index + 1;
        const needRightBorder = dayIndex < 7;

        const dayOffset = getDayOffset(weekIndex, dayIndex, currentWeek);
        const date = getDateDayNumber(dayOffset);
        const dateNum = date.format('D');

        const isActiveDay = weekIndex >= currentWeek;

        days.push(
          <div className={styles.cell} key={`day${index}`}>
            <div
              className={`${styles.day} ${needRightBorder ? styles.rightBorder : ''} ${
                isActiveDay ? styles.activeDay : ''
              }`}
            >
              {renderStages(date)}
              {dateNum}
            </div>
          </div>,
        );
      }

      return days;
    },
    [currentWeek, renderStages],
  );

  const renderWeeks = useMemo(() => {
    const weeks: React.ReactNode[] = [];

    for (let index = 0; index < weeksCount; index++) {
      const weekIndex = index + 1;
      const isCurrentWeek = currentWeek === weekIndex;

      weeks.push(
        <div key={`Week_${weekIndex}`} className={`${styles.week} ${isCurrentWeek ? styles.currentWeek : ''}`}>
          {renderDays(weekIndex)}
          {isCurrentWeek ? <Progress /> : null}
        </div>,
      );
    }

    return weeks;
  }, [currentWeek, renderDays, weeksCount]);

  const getStageName = useCallback((key: CLAN_WARS_STAGE | '') => {
    switch (key) {
      case CLAN_WARS_STAGE.PREPARATION:
        return t('Подготовка');
      case CLAN_WARS_STAGE.MATCHMAKING:
        return t('Подбор соперников');
      case CLAN_WARS_STAGE.WAR:
        return t('Битва');
      case CLAN_WARS_STAGE.REWARDING:
        return t('Подведение итогов');
      default:
        return '';
    }
  }, []);

  const renderLegend = useMemo(() => {
    const legendItems: React.ReactNode[] = [];

    stagesOrder.forEach((stageName) => {
      if (!Object.prototype.hasOwnProperty.call(stages, stageName)) {
        return;
      }

      if (
        stageName !== CLAN_WARS_STAGE.PREPARATION &&
        stageName !== CLAN_WARS_STAGE.MATCHMAKING &&
        stageName !== CLAN_WARS_STAGE.WAR &&
        stageName !== CLAN_WARS_STAGE.REWARDING
      ) {
        return;
      }

      legendItems.push(
        <div className={styles.legendStageWrapper} key={`LegendStage_${stageName}`}>
          <div className={styles.legendStage}>
            <div
              className={`${styles.stageBox} ${
                stageName === CLAN_WARS_STAGE.MATCHMAKING || stageName === CLAN_WARS_STAGE.REWARDING
                  ? styles.topBottomBorder
                  : ''
              }`}
            >
              {getInnerOverlay(stageName)}
            </div>
            <div className={styles.stageDescription}>
              <span className={styles.stageName}>{getStageName(stageName)}</span>
              <span className={styles.stageDuration}>{renderStageDuration(stageName)}</span>
            </div>
          </div>
        </div>,
      );
    });

    return legendItems;
  }, [getInnerOverlay, getStageName, renderStageDuration, stages]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.seasonTitle}>{t('Текущий раунд %(currentRound)s', { currentRound })}</div>

      <div className={styles.innerWrapper}>
        <div className={styles.dayTitleRow}>{renderDaysTitles}</div>
        {renderWeeks}
      </div>

      <div className={styles.legend}>
        {renderLegend}
        <span className={styles.legendDescription}>
          {t('Все временные интервалы в календаре указаны исходя из вашего часового пояса')}
        </span>
      </div>
    </div>
  );
};

export default memo(Calendar);
